import classNames from "classnames";
import React, { ReactElement } from "react";

import Icon from "../../../../../shared/components/design-system/Icon";
import {
  TextElement,
  Translatable,
  Translation,
  isTranslatable,
  useTranslate,
} from "../../../../../shared/components/translation";
import { importTranslationsBackground } from "../../../../../shared/translation/initialisation";
import { SearchableItem } from "../../../hooks/useSearch";
import { SearchableList } from "../SearchableList";
import styles from "./index.module.scss";
import english from "./translations/en-GB.json";

importTranslationsBackground("Table", { "en-GB": english });

// This version of Table is searchable, and will show sorting buttons and report when they're clicked but won't handle any sorting logic itself.
export const tableClassNames = {
  cards: styles.Cards,
  simple: styles.Simple,
} as const;
export interface TableColumn<RowType, IdKey extends string> {
  label: Translatable;
  id: IdKey;
  element: (row: RowType) => ReactElement | Translatable | null;
  isSortable?: boolean;
  sortNumeric?: boolean;
}

export type TableColumns<RowType, SortByColumnKey extends string> = Array<TableColumn<RowType, SortByColumnKey>>;

function Table<RowType extends SearchableItem, SortByColumnKey extends string>({
  columns,
  data,
  searchTerm = "",
  searchableFields = [],
  empty,
  sortByColumn,
  sortDirection,
  onSort,
  className,
  variant = "cards",
}: {
  data: RowType[];
  columns: TableColumns<RowType, SortByColumnKey>;
  searchTerm?: string;
  searchableFields?: Array<keyof RowType>;
  empty?: ReactElement;
  sortByColumn?: SortByColumnKey | null;
  sortDirection?: "ASC" | "DESC" | null;
  onSort?: (column: SortByColumnKey, direction: "ASC" | "DESC") => void;
  className?: string;
  variant?: "cards" | "simple";
}) {
  const translate = useTranslate();

  const handleSortColumn = (field: SortByColumnKey) => {
    onSort!(field, sortByColumn === field && sortDirection === "ASC" ? "DESC" : "ASC");
  };

  if (data.length === 0) return empty ?? null;

  return (
    <table className={`${styles.Table} ${className} ${variant ? tableClassNames[variant] : ""}`}>
      <thead>
        <tr>
          {columns.map(({ id, label, isSortable, sortNumeric }) => {
            const sorted = sortByColumn === id && !!sortDirection;
            const sortedAscending = sorted && sortDirection === "ASC";
            const sortedDescending = sorted && sortDirection === "DESC";
            return (
              <th
                key={id}
                scope="col"
                aria-sort={isSortable && sorted ? (sortedAscending ? "ascending" : "descending") : "none"}
                className={classNames(
                  {
                    "is-sortable": isSortable,
                    "is-sorted": sorted && sortDirection,
                    "is-sorted--asc": sortedAscending,
                    "is-sorted--desc": sortedDescending,
                  },
                  sorted ? styles.IsSorted : "",
                )}
              >
                {isSortable ? (
                  <button
                    className={styles.SortButton}
                    onClick={() => handleSortColumn(id)}
                    aria-label={translate(
                      `${label}, ${sortedAscending ? "Table.sortDescending" : "Table.sortAscending"}`,
                    )}
                  >
                    <Translation props={label} />
                    <span className={styles.Hover}>
                      <Icon
                        icon={
                          !sortedAscending
                            ? sortNumeric
                              ? "sortAscendingNumeric"
                              : "sortAscendingAlphabetical"
                            : sortNumeric
                              ? "sortDescendingNumeric"
                              : "sortDescendingAlphabetical"
                        }
                      />
                    </span>
                    {sorted ? (
                      <span className={styles.SortedHover}>
                        <Icon
                          icon={
                            sortedAscending
                              ? sortNumeric
                                ? "sortAscendingNumeric"
                                : "sortAscendingAlphabetical"
                              : sortNumeric
                                ? "sortDescendingNumeric"
                                : "sortDescendingAlphabetical"
                          }
                        />
                      </span>
                    ) : (
                      <span className={styles.UnsortedHover} />
                    )}
                  </button>
                ) : (
                  <TextElement tag="span" className={styles.UnsortableLabel} props={label} />
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        <SearchableList
          items={data}
          searchText={searchTerm}
          fields={searchableFields}
          component={(row) => (
            <tr className="Row">
              {columns.map(({ id, element, label }) => (
                <td key={id}>
                  <TextElement tag="span" className={styles.CellLabel} props={label} />
                  <CellContents el={element(row)} />
                </td>
              ))}
            </tr>
          )}
        />
      </tbody>
    </table>
  );
}

export default Table;

function CellContents({
  el,
}: {
  el: React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>> | Translatable | null;
}) {
  if (!el) return <TextElement tag="span" className={styles.CellEmpty} t="Table:none" />;
  if (isTranslatable(el)) return <Translation props={el} />;
  return el;
}
